import type { ZodSchema } from "zod";
import type { ApiError } from "@applicant-reviewer/api";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

// ---------------------------------------------------------------------------
// Auth token injection
// AuthProvider calls registerAuthTokenGetter() on mount to wire up auth.
// ---------------------------------------------------------------------------
let _getAuthToken: () => string | null = () => null;

export function registerAuthTokenGetter(fn: () => string | null): void {
  _getAuthToken = fn;
}

export function getAuthToken(): string | null {
  return _getAuthToken();
}

// ---------------------------------------------------------------------------
// API error classes
// ---------------------------------------------------------------------------
export class ApiRequestError extends Error {
  readonly statusCode: number;
  readonly code: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = "ApiRequestError";
    this.statusCode = error.statusCode;
    this.code = error.code;
  }
}

export class ApiValidationError extends Error {
  readonly issues: unknown[];
  readonly path: string;
  readonly rawResponse: unknown;

  constructor(issues: unknown[], path: string, rawResponse: unknown) {
    super(`API response validation failed for ${path}`);
    this.name = "ApiValidationError";
    this.issues = issues;
    this.path = path;
    this.rawResponse = rawResponse;
  }
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------
async function request<TSchema extends ZodSchema>(
  method: string,
  path: string,
  schema: TSchema,
  body?: unknown,
): Promise<TSchema["_output"]> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = _getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let apiError: ApiError;
    try {
      const errBody = (await response.json()) as { error?: string; message?: string };
      apiError = {
        message: errBody.message ?? errBody.error ?? response.statusText ?? "Unknown error",
        code: "UNKNOWN",
        statusCode: response.status,
      };
    } catch {
      apiError = {
        message: response.statusText || "Unknown error",
        code: "UNKNOWN",
        statusCode: response.status,
      };
    }
    throw new ApiRequestError(apiError);
  }

  // 204 No Content — no body to parse
  if (response.status === 204) {
    return undefined as TSchema["_output"];
  }

  const data: unknown = await response.json();
  const result = schema.safeParse(data);

  if (!result.success) {
    console.error(`API validation failed for ${path}:`, result.error.issues, data);
    throw new ApiValidationError(result.error.issues, path, data);
  }

  return result.data as TSchema["_output"];
}

// ---------------------------------------------------------------------------
// HTTP method helpers — hiring portal API (uses hiring token)
// ---------------------------------------------------------------------------
export const apiClient = {
  get<TSchema extends ZodSchema>(path: string, schema: TSchema): Promise<TSchema["_output"]> {
    return request("GET", path, schema);
  },

  post<TSchema extends ZodSchema>(path: string, schema: TSchema, body?: unknown): Promise<TSchema["_output"]> {
    return request("POST", path, schema, body);
  },

  put<TSchema extends ZodSchema>(path: string, schema: TSchema, body?: unknown): Promise<TSchema["_output"]> {
    return request("PUT", path, schema, body);
  },

  patch<TSchema extends ZodSchema>(path: string, schema: TSchema, body?: unknown): Promise<TSchema["_output"]> {
    return request("PATCH", path, schema, body);
  },

  delete<TSchema extends ZodSchema>(path: string, schema: TSchema): Promise<TSchema["_output"]> {
    return request("DELETE", path, schema);
  },
};
